import { useCallback } from "react";
import { IAssignmentEvent } from "@eppo/js-client-sdk";
import {
  Context,
  MiddlewareFunction,
  User,
} from "@ht-sdks/events-sdk-js-browser";
import { useSession } from "next-auth/react";
import Cookies from "js-cookie";
import { getAnalytics } from "src/utils/getAnalytics";
import { isClient } from "src/utils/isClient";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import {
  CatalogProduct,
  HomepageSingleCategoryProduct,
  SingleProduct,
  SingleCartProductVariation,
  ProductVariation,
} from "src/types/graphql.d";
import { fixFemaleOrMale } from "src/utils/fixFemaleOrMale";
import {
  SEGMENT_EVENTS,
  Filter,
  SegmentProductListMeta,
  SegmentCatalogProduct,
  SegmentHomepageSingleCategoryProduct,
  SegmentCouponTracking,
  getFormName,
  SEGMENT_DATA_UNAVAILABLE,
  SEGMENT_PROMOTION_TYPE,
  sha256Email,
} from "src/segment";
import {
  Cart,
  LineItem,
  ProductPrice,
  RawProductAttribute,
  AddressInput,
} from "src/types/ctgraphql.d";
import { Iso2LetterLocale, Locale } from "src/lib/Intl";
import {
  getCTAttribute,
  getCouponCodesWithDiscounts,
  getDiscountCode,
  getDiscountCodes,
  getShippingCost,
  getTrackingCartItems,
  hasDiscountCode,
  itemLabels,
  Labels,
  CouponCodeInfo,
} from "src/utils/ct/attributeHelper";
import { formatStandardPrice } from "src/utils/ct/priceHelper";
import { getCartItemsCount } from "src/utils/ct/cart-helper";
import { getLineItemPrice } from "src/utils/getRegularPrice";
import { useRouter } from "next/router";
import { useFreeShippingThreshold } from "src/hooks/use-free-shipping-threshold";
import { CarouselType } from "src/types/Carousels.d";
import { locale, storeConfig } from "src/config/app-config";
import {
  GATED_POPUP_REDIRECT_DATA,
  GATED_POPUP_SOURCE,
  GATED_SEARCH_PRODUCT_CARD,
} from "src/constants/gatedItem";
import { getLineItemsDiscountPrice } from "src/utils/ct/discountCodeHelper";
import { CategoryPreferences } from "@ht-sdks/consent-manager/types/types";
import { TrackingShippingMethods } from "src/molecules/ShippingMethods/ShippingMethods";
import { getTalonOneDiscountAmount } from "src/utils/talon-one";
import type { CheckoutTrackingData } from "src/types/checkoutTracking";
import { pushToDataLayer } from "src/tracking/googleTagManager";
import { SignLocation, SignPlatform } from "src/tracking/segment";
import type { ProductListType } from "src/types/product-listing";
import { PRODUCT_LISTING_TYPE } from "src/utils/product-list-context-keys";
import type { HomePageType } from "src/types/HomePageType";
import { isProductListType } from "src/utils/type-guards";
import { getSessionStorageJSON } from "src/utils/helper/sessionStorage";

export type TrackFnReturnType =
  | Promise<void>
  | Promise<Context>
  | Promise<Context[]>;

export type FilterAction = "filter" | "sort" | "next_page";

// Backward compat: numeric keys map to PLPType enum values
export const PLP_PAGE_TYPE: Record<number, string> = {
  0: "brand", // PLPType.BRAND_PLP
  1: "icons", // PLPType.ICONS_PLP
  2: "category", // PLPType.CATEGORY_PLP
  3: "new_in", // PLPType.NEW_IN
  4: "hot_deals", // PLPType.HOT_DEALS
  5: "clearance", // PLPType.CLEARANCE_PLP
  6: "designer", // PLPType.DESIGNER_PLP
  7: "collection", // PLPType.COLLECTIONS_PLP
  8: "search_catalog", // PLPType.SEARCH_PLP
  9: "shoptype", // PLPType.SHOP_TYPE
};

export enum PromotionEventType {
  click = "click",
  view = "view",
}

export type productListClickedProps = SegmentProductListMeta & {
  gatedActionType?: string;
  actionSource?: string;
  redirectTo?: string;
  promo_path?: string;
};

export type SegmentProductClicked = {
  position: number;
  product: CatalogProduct | HomepageSingleCategoryProduct;
  productListMeta: SegmentProductListMeta;
  gatedActionType?: string;
  actionSource?: string;
  redirectTo?: string;
  promo_path?: string;
  query?: string;
};

export type GatedPopupData = {
  pageName: string;
  actionSource: string;
  carouselType?: string;
  shopType: string;
  productName?: string;
  productId?: string;
  productImageUrl?: string;
  brandName?: string;
  brandId?: string;
  brandImageUrl?: string;
  redirectTo?: string;
  level?: string;
  tab?: string;
};

export type GatedPopupRedirectData = {
  promo_type?: string;
  shop_type?: string;
  gated_action_type?: string;
  redirect_to?: string;
};

export interface Promotion {
  creative?: string;
  eventType: PromotionEventType;
  id?: string;
  name?: string | null;
  position?: string;
  path?: string;
  promotionType?: SEGMENT_PROMOTION_TYPE;
  gated?: boolean;
}

enum TrackingLocale {
  "en" = "en-EN",
  "de" = "de-DE",
  "fr" = "fr-FR",
  "nl" = "nl-NL",
  "zz" = "en-EN",
  "en-gb" = "en-GB",
  "es-es" = "es-ES",
  "it-it" = "it-IT",
  "en-dk" = "en-DK",
  "en-se" = "en-SE",
  "pl-pl" = "pl-PL",
  "de-at" = "de-AT",
  "fr-be" = "fr-BE",
  "nl-be" = "nl-BE",
  "en-us" = "en-US",
}

enum CheckoutSteps {
  STEP_1 = "Personal Details",
  STEP_2 = "Payment Method",
}

export type GeneralObject = Record<string, any>;

type UserUpdateData = {
  firstName?: string;
  lastName?: string;
  gender?: "female" | "male";
  birthday?: string;
  phoneNumber?: string;
  address?: {
    postalCode?: string;
    country?: string;
    city?: string;
    street?: string;
    state?: string;
  };
};

type AuthBannerData = {
  page_type?: ProductListType | HomePageType;
  brand?: string;
  category?: string;
  category_slug?: string;
  shop_type?: string;
};

export type SearchResultProduct = {
  position: number;
  product_shop_type: string[];
  product_kids_gender: string[];
  product_id: string;
  // product_variation: string;
  name: string;
  brand: string;
  brand_id: string;
  price: number;
  regular_price: number;
  product_discount: number;
  sizes_available: string[];
  category: string[];
  category_id: string[];
  labels: string[];
  url: string;
};

export type SearchResultDisplayedProps = {
  query?: string;
  result_id?: string;
  num_categories_returned: number;
  num_products_returned: number;
  num_brands_returned: number;
  num_collections_returned: number;
  search_result_categories: string[];
  search_result_products: SearchResultProduct[];
  search_result_brands: string[];
  search_result_collections: string[];
};

type FirstCheckoutStepParams = {
  cartId: string;
  shippingMethodId: string | undefined;
  shippingMethodName: string | undefined;
  email: string | undefined;
  billingAddress: AddressInput;
  shippingAddress?: AddressInput;
};

type UseSegmentReturnType = {
  user?: User;
  segmentIdentify: (
    additionalOptions: IdentifyAdditionalOptions
  ) => TrackFnReturnType;

  segmentSignedIn: () => TrackFnReturnType;
  segmentSignedOut: () => Promise<void>;
  segmentSignedUp: () => TrackFnReturnType;
  segmentCheckoutSignedUp: ({
    email,
    location,
  }: {
    email: string;
    location?: SignLocation;
  }) => TrackFnReturnType;
  segmentBrandInfoOpened: (data: {
    pageType: string | null | undefined;
    brandName: string | null | undefined;
  }) => TrackFnReturnType;
  segmentBrandConsciousHowWeRateClicked: (data: {
    pageType: string | null | undefined;
    brandName: string | null | undefined;
  }) => TrackFnReturnType;
  brandFavourited: (data: {
    brandName: string | null | undefined;
    brandId: string | null | undefined;
  }) => TrackFnReturnType;
  brandUnfavourited: (data: {
    brandName: string | null | undefined;
    brandId: string | null | undefined;
  }) => TrackFnReturnType;
  brandFavouritedWhenLoggedOut: (data: {
    brandName: string | null | undefined;
    brandId: string | null | undefined;
  }) => TrackFnReturnType;

  segmentProductListClicked: (
    data: productListClickedProps
  ) => TrackFnReturnType;

  segmentProductListFilterOpened: (filterName: string) => TrackFnReturnType;
  segmentCartViewed: (cart: Cart) => TrackFnReturnType;
  checkoutStarted: (
    cart: Cart,
    shippingMethods?: TrackingShippingMethods
  ) => TrackFnReturnType;
  segmentProductClicked: (args: SegmentProductClicked) => TrackFnReturnType;

  checkoutFirstStepCompleted: (
    data: FirstCheckoutStepParams
  ) => TrackFnReturnType;

  checkoutSecondStepStarted: (cart: Cart) => TrackFnReturnType;

  segmentSecondCheckoutStepCompleted: (
    cartId: string,
    paymentMethod?: string
  ) => TrackFnReturnType;

  segmentOrderCompleted: (data: {
    trackingData: CheckoutTrackingData;
    isOldCustomer: boolean;
  }) => TrackFnReturnType;

  segmentCouponApplied: (
    data: SegmentCouponTracking & { couponDiscountAbsolute: number }
  ) => TrackFnReturnType;
  segmentCouponDenied: (
    data: SegmentCouponTracking & { couponDenied: string }
  ) => TrackFnReturnType;
  segmentCouponEntered: (data: SegmentCouponTracking) => TrackFnReturnType;
  segmentCouponRemoved: (data: SegmentCouponTracking) => TrackFnReturnType;

  segmentFormError: (
    formField: string,
    formErrorMessage: string,
    formName?: string
  ) => TrackFnReturnType;
  segmentFormTracking: (
    formField: string,
    formName?: string
  ) => TrackFnReturnType;
  segmentOptIn: (
    optIn: boolean,
    email: string | null | undefined,
    origin?: string
  ) => Promise<void | Context>;

  segmentPageViewed: (data: PageViewedProps) => TrackFnReturnType;
  segmentPasswordUpdated: () => TrackFnReturnType;
  segmentProductOutOfStock: (
    product: SingleProduct | SingleCartProductVariation,
    size?: string | null
  ) => TrackFnReturnType;
  segmentProductAddedOrRemoved: (
    lineItem: LineItem,
    type: "productAdded" | "productRemoved",
    countryCode: Iso2LetterLocale | null,
    quantity?: number,
    cartData?: {
      product_count?: number;
      cart_total?: number;
      totalPrice?: number;
      price?: number;
      discountPrice?: number | null;
    },
    lineItems?: LineItem[]
  ) => TrackFnReturnType;
  segmentProductSizeSelected: (
    product: SingleProduct,
    size?: string | null,
    resultId?: string
  ) => TrackFnReturnType;
  segmentProductSizeSelectorClicked: (
    product: SingleProduct,
    resultId?: string
  ) => TrackFnReturnType;
  segmentProductViewed: (
    product: SingleProduct,
    isLoggedIn?: boolean,
    gatedActionType?: string,
    redirectUrl?: string,
    actionSource?: string
  ) => TrackFnReturnType;
  segmentPromotionClickedOrViewed: (
    data: Promotion & {
      tab?: string;
      gatedActionType?: string;
      shopType?: string;
      brand?: string;
      brandId?: string;
      redirectTo?: string;
    }
  ) => TrackFnReturnType;
  segmentUserEmailUpdated: () => TrackFnReturnType;
  segmentProductOutOfStockCT: (
    lineItem: LineItem,
    warning: string,
    countryCode: Iso2LetterLocale | null
  ) => TrackFnReturnType;
  segmentDomainChanged: (
    domainName: string,
    callback: () => void
  ) => TrackFnReturnType;
  segmentToasterNotificationDisplayed: (
    promo_name: string | undefined,
    promo_path?: string
  ) => TrackFnReturnType;
  segmentToasterNotificationDismissed: (
    promo_name: string | undefined,
    promo_path?: string
  ) => TrackFnReturnType;
  segmentToasterNotificationClicked: (
    promo_name: string | undefined,
    promo_path?: string
  ) => TrackFnReturnType;
  segmentSearchOverlayOpened: () => TrackFnReturnType;
  segmentSearchOverlayClosed: (
    closedVia: "click x" | "click outside"
  ) => TrackFnReturnType;
  segmentSearchRecommendationClicked: (
    search_rec_type: CarouselType,
    search_rec_name: string,
    search_rec_position: number
  ) => TrackFnReturnType;
  segmentViewSearchAllBrands: () => TrackFnReturnType;
  segmentViewSearchResultPage: () => TrackFnReturnType;
  trackForgotPassword: () => TrackFnReturnType;
  segmentPdpInfoBoxOpened: (boxName: string) => TrackFnReturnType;
  segmentUserProfileUpdated: (userData: UserUpdateData) => TrackFnReturnType;
  segmentViewedAuthBanner: (page_name?: string) => TrackFnReturnType;
  segmentAuthBannerSelect: (
    data: AuthBannerData & {
      type: "sign_up" | "sign_in";
    }
  ) => TrackFnReturnType;
  segmentAuthBannerClosed: (
    data: AuthBannerData & {
      popup_type: "sign_up" | "sign_in";
    }
  ) => TrackFnReturnType;
  segmentGuestOptInBannerDismissed: (page_name?: string) => TrackFnReturnType;
  segmentGuestOptInBannerViewed: (page_name?: string) => TrackFnReturnType;
  segmentGuestOptInBannerCtaClick: (page_name?: string) => TrackFnReturnType;
  segmentGuestOptInPopupDismissed: (page_name?: string) => TrackFnReturnType;
  segmentTrackingError: (data: {
    error_type: string;
    error_message: string;
  }) => TrackFnReturnType;
  trackingGooglePayButtonClicked: (card_id: string) => TrackFnReturnType;
  trackingCategoryItemClicked: (
    category_slug: string,
    category_name: string
  ) => TrackFnReturnType;
  eppoRandomizedAssigment: (assigment: IAssignmentEvent) => TrackFnReturnType;
  segmentMenuNavClicked: (
    menuType: string,
    menuClickType: string,
    menuClickName: string,
    menuSectionName?: string
  ) => TrackFnReturnType;
  segmentOptInDeeplinkViewed: () => TrackFnReturnType;
  segmentGatedPopupOpened: (data: GatedPopupData) => TrackFnReturnType;
  segmentGatedPopupLoginClicked: (data: GatedPopupData) => TrackFnReturnType;
  segmentGatedPopupRegisterClicked: (data: GatedPopupData) => TrackFnReturnType;
  segmentGatedPopupClosed: (data: GatedPopupData) => TrackFnReturnType;
  segmentUserDeleted: () => TrackFnReturnType;
  listViewAllSelected: (
    item: {
      title: string;
      num_items: number;
    },
    button_position: "header" | "bottom"
  ) => TrackFnReturnType;
  searchResultDisplayed: (
    props: SearchResultDisplayedProps
  ) => TrackFnReturnType;
  searchResultClicked: (
    props: SearchResultDisplayedProps & {
      position_clicked: number;
      product_id_clicked?: string;
      category_id_clicked?: string;
      brand_id_clicked?: string;
      collections_id_clicked?: string;
      gate_level?: string;
      gated?: boolean;
    }
  ) => TrackFnReturnType;
  searchAbandoned: (
    props: Partial<SearchResultDisplayedProps>
  ) => TrackFnReturnType;
  productsSearched: (query: string) => TrackFnReturnType;
  gatedSearchResultRedirected: (props: {
    actionSource: string;
    gatedActionType?: string;
    brandName?: string;
    shopType?: string;
    gateLevel?: string;
    redirectTo: string;
  }) => TrackFnReturnType;
  gatedPopupRedirected: (data: GatedPopupRedirectData) => TrackFnReturnType;
  consentStatusUpdated: (preferences: CategoryPreferences) => TrackFnReturnType;
  continueAsGuest: () => TrackFnReturnType;
};

interface IdentifyAdditionalOptions {
  sign_in_platform?: AuthProvider;
  sign_up_platform?: AuthProvider;
  [key: string]: any;
}

interface TrackingCartProduct {
  brand: unknown;
  brand_id: unknown;
  category: never[];
  category_id: never[];
  image_url: unknown;
  labels: unknown;
  name: unknown;
  price: number;
  product_id: unknown;
  product_size: unknown;
  quantity: number;
  regular_price: number;
  sku?: string | null;
  url: string;
  variant: unknown;
  product_variation: unknown;
}

interface TrackingCartData {
  request_source: "appAndroid" | "appIos" | "webReact";
  cart_id: string;
  currency: string;
  cart_total_products: number;
  cart_total_value: number;
  products: TrackingCartProduct[];
  discounts: string[];
  coupons: CouponCodeInfo[];
  shipping_tax_rate?: number;
}

interface PageViewedProps {
  pathName: string;
  locale: Locale;
  shopType?: string;
  cartData?: {
    product_count?: number;
    cart_total?: number;
  };
  productAvailable?: boolean;
  gtmProperties?: GeneralObject;
}

type GA_COOKIES = { [key: string]: string };

const isIOSApp =
  typeof webkit !== "undefined" &&
  typeof webkit.messageHandlers?.track !== "undefined";

const isAndroidApp = typeof segment_webview !== "undefined";

const isNativeApp = isAndroidApp || isIOSApp;

export const requestSource = {
  get request_source(): "appAndroid" | "appIos" | "webReact" {
    const searchParams = new URLSearchParams(location.search);
    const isFromAndroid = searchParams.get("app") === "android";
    if (isAndroidApp || isFromAndroid) return "appAndroid";
    return "webReact";
  },
};

export const localeAdapter = (locale: Locale): TrackingLocale => {
  return TrackingLocale[locale];
};

const selectedCountry: string = localeAdapter(locale) ?? TrackingLocale.en;

const selectedCountryMiddleware: MiddlewareFunction = ({ payload, next }) => {
  const event = payload?.obj;

  if (event) {
    event.context = {
      ...event.context,
      traits: {
        ...event.context?.traits,
        selected_country: selectedCountry,
      },
    };
  }

  next(payload);
};

export const selectedCountryTraitRegistered = ((): boolean => {
  if (typeof window === "undefined" || isNativeApp) return false;
  if (!window.htevents?.addSourceMiddleware) return false;

  void window.htevents.addSourceMiddleware(selectedCountryMiddleware);

  return true;
})();

export const getFilterDiff = (
  filters: Filter[],
  prevFilters: Filter[] | undefined
): Filter[] => {
  if (!prevFilters) return filters;

  return filters.filter((f) => {
    return !prevFilters.find((pf) => {
      if (f.type === "min_price" || f.type === "max_price") {
        return (
          pf.type === f.type && pf.value && f.value && +pf.value === +f.value
        );
      }
      return pf.type === f.type && pf.value === f.value;
    });
  });
};

const getRegularPrice = (
  prices: ProductPrice[] | undefined | null,
  cart: Cart
) => {
  const localPrice = prices?.find(
    (price) => price?.country === cart?.shippingAddress?.country
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  const regularPriceInCents = localPrice?.custom?.customFieldsRaw?.find(
    (field) => field.name === "list_price"
  )?.value.centAmount;

  return regularPriceInCents / 100;
};

const getSelectedSize = (attributesRaw: RawProductAttribute[] | undefined) =>
  getCTAttribute("size_value", attributesRaw);

const getTrackingCartProducts = (cart: Cart): TrackingCartProduct[] => {
  return cart?.lineItems.map((item) => {
    const attributesRaw = item.variant?.attributesRaw;
    const prices = item.variant?.prices;

    return {
      brand: getCTAttribute("brand_name", attributesRaw),
      brand_id: getCTAttribute("woo_brand_id", attributesRaw),
      category: [],
      category_id: [],
      image_url: getCTAttribute("main_image", attributesRaw),
      labels: getCTAttribute("labels", attributesRaw),
      name: getCTAttribute("name", attributesRaw),
      price: item.price.value.centAmount / 100,
      tax_rate: item?.taxRate?.amount,
      product_id: getCTAttribute("woo_product_id", attributesRaw),
      ot_product_id: getCTAttribute("ot_product_id", attributesRaw),
      product_size: getSelectedSize(attributesRaw),
      quantity: item.quantity as number,
      regular_price: getRegularPrice(prices, cart),
      sku: item?.variant?.sku,
      url: `/product/${item.productSlug as string}`,
      variant: getSelectedSize(attributesRaw),
      product_variation: getCTAttribute("product_id", attributesRaw),
      // product_discount: null,
      // product_kids_gender: null,
      // product_shop_type: null,
    };
  });
};

const toTrackingCoupons = (coupons: CouponCodeInfo[]): CouponCodeInfo[] =>
  coupons.map((coupon) => ({
    ...coupon,
    coupon_code: coupon.coupon_code.toUpperCase(),
  }));

const getTrackingCartData = (cart: Cart): TrackingCartData => {
  return {
    ...requestSource,
    cart_id: cart?.id,
    currency: (cart?.totalPrice?.currencyCode as string) || "",
    cart_total_products: cart?.lineItems?.reduce(
      (partial, item) => +item.quantity + partial,
      0
    ),
    shipping_tax_rate: cart.shippingInfo?.taxRate?.amount,
    cart_total_value: (cart?.taxedPrice?.totalGross || cart?.totalPrice)
      ?.centAmount
      ? (cart?.taxedPrice?.totalGross || cart?.totalPrice)?.centAmount / 100
      : 0,
    discounts: getDiscountCodes(cart),
    coupons: toTrackingCoupons(getCouponCodesWithDiscounts(cart)),
    products: getTrackingCartProducts(cart),
  };
};

const getAvailableSizes = (variations?: ProductVariation[]) =>
  (variations ?? []).filter((v) => v.stock_quantity > 0).map((v) => v.size);

const isClientWrapper = <T extends (...args: any) => any>(
  fn: (...args: Parameters<T>) => ReturnType<T>
) => {
  if (!isClient) return () => new Promise<void>((resolve) => resolve());
  return fn;
};

const getGenericFilterName = (filterName: string): string => {
  switch (filterName) {
    case "brand.meta":
      return "brands";
    case "color.meta":
      return "colors";
    case "container_sizes.meta":
      return "sizes";
    case "promotion_filter.meta":
      return "promotions";
    case "style.meta":
      return "styles";
    case "pattern.meta":
      return "patterns";
    case "brand.good_on_you_info.meta":
      return "conscious";
    case "cat_slug":
    case "categories.meta":
      return "categories";
    default:
      return filterName;
  }
};

function segmentNativeAppHandler({
  eventName,
  eventParams,
  eventType,
}:
  | {
      eventName: string;
      eventParams: Record<string, unknown>;
      eventType: "track" | "screen";
    }
  | {
      eventName?: undefined;
      eventParams: Record<string, unknown>;
      eventType: "identify";
    }): Promise<void> {
  try {
    if (isAndroidApp) {
      const params = JSON.stringify(eventParams);
      switch (eventType) {
        case "identify":
          segment_webview.identify(params);
          break;
        case "track":
          segment_webview.track(eventName, params);
          break;
        case "screen":
          segment_webview.screen(eventName, params);
          break;
      }
    }

    if (isIOSApp) {
      const json = JSON.stringify({
        messageType: eventType,
        eventName,
        eventParams,
      });
      switch (eventType) {
        case "identify":
          webkit.messageHandlers.identify.postMessage(json);
          break;
        case "track":
          webkit.messageHandlers.track.postMessage(json);
          break;
        case "screen":
          webkit.messageHandlers.screen.postMessage(json);
          break;
      }
    }

    return new Promise<void>((resolve) => resolve());
  } catch (error) {
    console.error(error);
    return new Promise<void>((_, rejected) => rejected(error));
  }
}

export function useSegment() {
  const { htevents } = getAnalytics();
  const gatedRedirectData = getSessionStorageJSON(GATED_POPUP_REDIRECT_DATA);
  let user;

  if (htevents?.user) {
    user = htevents.user();
  }

  const { data: session } = useSession();
  const { asPath: path, query } = useRouter();
  const freeShippingThreshold = useFreeShippingThreshold();

  const sendSegmentTrackEvent = useCallback(
    async (
      eventName: SEGMENT_EVENTS,
      properties: GeneralObject,
      callback?: () => void
    ): Promise<Context> => {
      if (isNativeApp) {
        return new Promise<Context>((resolve) => {
          segmentNativeAppHandler({
            eventName,
            eventParams: properties,
            eventType: "track",
          }).finally(() => resolve({} as Context));
        });
      } else {
        return window.htevents?.track(
          eventName,
          properties,
          {
            context: { locale: localeAdapter(locale) },
          },
          callback
        );
      }
    },
    [locale]
  );

  const segmentIdentify = isClientWrapper(
    (additionalOptions: IdentifyAdditionalOptions) => {
      if (!session?.user?.email)
        return new Promise<void>((resolve) => resolve());

      const { member_id, email, gender } = session?.user;
      const { sign_in_platform, sign_up_platform } = additionalOptions;

      const traits = {
        email,
        // TODO: Delete temporary fix in https://otrium.atlassian.net/browse/TT-2482
        gender: fixFemaleOrMale(gender),
        ...(member_id ? { user_id_type: "member" } : {}),
        ...requestSource,
        ...additionalOptions,
        sign_in_platform:
          sign_in_platform === AuthProvider.CREDENTIALS
            ? "email"
            : sign_in_platform === AuthProvider.GOOGLE_ONE_TAP
            ? SignPlatform.GOOGLE
            : sign_in_platform,
        sign_up_platform:
          sign_up_platform === AuthProvider.CREDENTIALS_REGISTER
            ? "email"
            : sign_up_platform === AuthProvider.GOOGLE_ONE_TAP
            ? SignPlatform.GOOGLE
            : sign_up_platform,
        selected_country: selectedCountry,
      };

      return htevents.identify(member_id, traits, {
        context: { locale: localeAdapter(locale) },
      });
    }
  );

  const segmentSignedIn = isClientWrapper(() => {
    if (!session?.user) return new Promise<void>((resolve) => resolve());

    const { platform, email } = session.user;
    const isCheckoutPath = path.includes("checkout");
    const hashedEmail = email ? sha256Email(email) : undefined;
    const location =
      session.user.signInPlatform === AuthProvider.GOOGLE_ONE_TAP
        ? SignLocation.ONE_TAP
        : isCheckoutPath
        ? SignLocation.CHECKOUT
        : SignLocation.SIGN_IN_PAGE;

    pushToDataLayer({
      event: "user_login",
      user_id: session?.user?.user_id,
      eid_sha256: hashedEmail,
      authentication_method: platform,
      location,
    });

    return sendSegmentTrackEvent(SEGMENT_EVENTS.signedIn, {
      ...requestSource,
      sign_in_platform: platform,
      email,
      gated: !!(query?.source === GATED_POPUP_SOURCE || gatedRedirectData),
      location,
    });
  });

  const segmentSignedUp = isClientWrapper(() => {
    if (!session?.user) return new Promise<void>((resolve) => resolve());

    const { platform, email } = session.user;
    const isCheckoutPath = path.includes("checkout");
    const hashedEmail = session?.user?.email
      ? sha256Email(session?.user?.email)
      : undefined;
    const location =
      session.user.signInPlatform === AuthProvider.GOOGLE_ONE_TAP
        ? SignLocation.ONE_TAP
        : isCheckoutPath
        ? SignLocation.CHECKOUT
        : SignLocation.SIGN_UP_PAGE;

    pushToDataLayer({
      event: "user_register",
      user_id: session?.user?.user_id,
      eid_sha256: hashedEmail,
      authentication_method: platform,
      location,
    });

    return sendSegmentTrackEvent(SEGMENT_EVENTS.signedUp, {
      ...requestSource,
      sign_up_platform: platform,
      email,
      gated: !!(query?.source === GATED_POPUP_SOURCE || gatedRedirectData),
      location,
    });
  });

  const segmentCheckoutSignedUp = isClientWrapper(
    ({
      email,
      location = SignLocation.CHECKOUT,
    }: {
      email: string;
      location?: SignLocation;
    }) => {
      if (!session?.user) return new Promise<void>((resolve) => resolve());

      const hashedEmail = email ? sha256Email(email) : undefined;

      pushToDataLayer({
        event: "user_register",
        user_id: session?.user?.user_id,
        eid_sha256: hashedEmail,
        authentication_method: SignPlatform.EMAIL,
        location,
      });

      return sendSegmentTrackEvent(SEGMENT_EVENTS.signedUp, {
        ...requestSource,
        sign_up_platform: SignPlatform.EMAIL,
        email,
        gated: false,
        location,
      });
    }
  );

  const segmentSignedOut = isClientWrapper(() => {
    return new Promise<void>((resolve) => {
      void sendSegmentTrackEvent(
        SEGMENT_EVENTS.signedOut,
        requestSource,
        () => {
          void htevents.reset();
          resolve();
        }
      );
    });
  });

  const segmentBrandInfoOpened = isClientWrapper(
    ({
      pageType,
      brandName,
    }: {
      pageType: string | null | undefined;
      brandName: string | null | undefined;
    }) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.brandInfoOpened, {
        ...requestSource,
        brand_name: brandName,
        page_type: pageType,
      })
  );

  const segmentBrandConsciousHowWeRateClicked = isClientWrapper(
    ({
      pageType,
      brandName,
    }: {
      pageType: string | null | undefined;
      brandName: string | null | undefined;
    }) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.brandConsciousHowWeRateClicked, {
        ...requestSource,
        brand_name: brandName,
        page_type: pageType,
      })
  );

  const brandFavourited = isClientWrapper(
    ({
      brandName,
      brandId,
    }: {
      brandName: string | null | undefined;
      brandId: string | null | undefined;
    }) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.brandFavourited, {
        ...requestSource,
        brand_name: brandName,
        brand_id: brandId,
        email: session?.user?.email,
      })
  );

  const brandUnfavourited = isClientWrapper(
    ({
      brandName,
      brandId,
    }: {
      brandName: string | null | undefined;
      brandId: string | null | undefined;
    }) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.brandUnfavourited, {
        ...requestSource,
        brand_name: brandName,
        brand_id: brandId,
        email: session?.user?.email,
      })
  );

  const brandFavouritedWhenLoggedOut = isClientWrapper(
    ({
      brandName,
      brandId,
    }: {
      brandName: string | null | undefined;
      brandId: string | null | undefined;
    }) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.brandFavouritedWhenLoggedOut, {
        ...requestSource,
        brand_name: brandName,
        brand_id: brandId,
        email: session?.user?.email,
      })
  );

  const segmentProductClicked = useCallback(
    (...args: Parameters<UseSegmentReturnType["segmentProductClicked"]>) =>
      isClientWrapper<UseSegmentReturnType["segmentProductClicked"]>(
        ({
          position,
          product,
          productListMeta: { listFormat, listId, listType, shopType },
          gatedActionType,
          actionSource,
          redirectTo,
          promo_path,
          query,
        }: SegmentProductClicked) => {
          const {
            price: catalogProductPrice,
            regular_price,
            available_sizes,
          } = product as SegmentCatalogProduct;

          const { price_float, regular_price_float, sizes_available } =
            product as SegmentHomepageSingleCategoryProduct;

          const price = catalogProductPrice || price_float;
          const regularPrice = regular_price || regular_price_float;
          const sizesAvailable = available_sizes || sizes_available || [];
          const pathValue = gatedActionType ? { promo_path } : { path };

          return sendSegmentTrackEvent(
            (gatedActionType
              ? actionSource === GATED_SEARCH_PRODUCT_CARD
                ? SEGMENT_EVENTS.gatedSearchResultRedirected
                : SEGMENT_EVENTS.gatedPopupRedirected
              : SEGMENT_EVENTS.productClicked) as SEGMENT_EVENTS,
            {
              ...requestSource,
              brand: product.brand.name,
              brand_id: product.brand.id,
              category_id: product.translated_category_ids,
              category: product.translated_category_names,
              image_url: product.thumbnail,
              labels: (product.labels || []).map(({ value }) => value),
              list_format: listFormat,
              list_id: listId,
              list_type: listType,
              name: product.name,
              ...pathValue,
              position,
              price,
              product_discount:
                product?.product_discount ||
                Math.round((regularPrice - price + Number.EPSILON) * 100) / 100,
              product_id: product.id,
              product_kids_gender: product.product_shop_type?.includes("kids")
                ? product.product_kids_gender
                : undefined,
              product_shop_type: product.product_shop_type,
              regular_price: regularPrice,
              shop_type: shopType,
              sizes_available: sizesAvailable,
              url: `/product/${product.slug}`,
              gated_action_type: gatedActionType,
              gate_level: product.gate?.level || null,
              action_source: actionSource,
              ...(redirectTo && { redirect_to: redirectTo }),
              query,
            }
          );
        }
      )(...args),
    [path, sendSegmentTrackEvent]
  );

  const segmentProductListClicked = isClientWrapper(
    ({
      listFormat,
      listId,
      listType,
      shopType,
      gatedActionType,
      actionSource,
      redirectTo,
      promo_path,
    }: productListClickedProps) => {
      const pathValue = gatedActionType ? { promo_path } : { path };

      return sendSegmentTrackEvent(
        (gatedActionType
          ? SEGMENT_EVENTS.gatedPopupRedirected
          : SEGMENT_EVENTS.productListClicked) as SEGMENT_EVENTS,
        {
          ...requestSource,
          list_id: listId,
          list_type: listType,
          list_format: listFormat,
          ...pathValue,
          gated_action_type: gatedActionType,
          shop_type: shopType,
          action_source: actionSource,
          ...(redirectTo && { redirect_to: redirectTo }),
        }
      );
    }
  );

  const segmentCartViewed = isClientWrapper((cart: Cart) => {
    const discount = getLineItemsDiscountPrice(
      cart?.discountCodes,
      cart.lineItems
    );

    pushToDataLayer({
      event: "view_cart",
      cart_id: cart.id,
      cart_value: formatStandardPrice(cart?.totalPrice),
      shipping_method: cart.shippingInfo?.shippingMethod?.name,
      shipping_method_id: cart.shippingInfo?.shippingMethod?.id,
      shipping_costs: cart?.shippingInfo
        ? getShippingCost(cart?.shippingInfo)
        : undefined,
      coupon_event_type: hasDiscountCode(cart) ? "added" : "not entered",
      coupon_code: getDiscountCode(cart),
      coupon_amount: discount
        ? formatStandardPrice(discount)
        : getTalonOneDiscountAmount(cart),
      discounts: getDiscountCodes(cart),
      shipping_tax_rate: cart.shippingInfo?.taxRate?.amount,
      items: getTrackingCartItems(cart?.lineItems, storeConfig),
    });

    return sendSegmentTrackEvent(SEGMENT_EVENTS.cartViewed, {
      ...getTrackingCartData(cart),
      free_shipping_threshold: freeShippingThreshold
        ? freeShippingThreshold.centAmount / 100
        : null,
    });
  });

  const checkoutStarted = isClientWrapper(
    (cart: Cart, shippingMethods: TrackingShippingMethods) => {
      const discount = getLineItemsDiscountPrice(
        cart?.discountCodes,
        cart.lineItems
      );

      const hashedEmail = session?.user?.email
        ? sha256Email(session?.user?.email)
        : undefined;

      pushToDataLayer({
        event: "checkout",
        checkout_step: 1,
        checkout_step_name: CheckoutSteps.STEP_1,
        checkout: shippingMethods.map((method) => ({
          delivery_option: method.name,
          shipping_cost: formatStandardPrice(method?.price),
        })),
        cart_id: cart.id,
        cart_value: (cart?.taxedPrice?.totalGross || cart?.totalPrice)
          ?.centAmount
          ? (cart?.taxedPrice?.totalGross || cart?.totalPrice)?.centAmount / 100
          : 0,
        shipping_method: cart.shippingInfo?.shippingMethod?.name,
        shipping_method_id: cart.shippingInfo?.shippingMethod?.id,
        shipping_costs: cart?.shippingInfo
          ? getShippingCost(cart?.shippingInfo)
          : undefined,
        coupon_event_type: hasDiscountCode(cart) ? "added" : "not entered",
        coupon_code: getDiscountCode(cart),
        coupon_amount: discount
          ? formatStandardPrice(discount)
          : getTalonOneDiscountAmount(cart),
        discounts: getDiscountCodes(cart),
        shipping_tax_rate: cart.shippingInfo?.taxRate?.amount,
        items: getTrackingCartItems(cart?.lineItems, storeConfig),
        eid_sha256: hashedEmail,
      });

      return sendSegmentTrackEvent(
        SEGMENT_EVENTS.checkoutStarted,
        getTrackingCartData(cart)
      );
    }
  );

  const checkoutFirstStepCompleted = isClientWrapper(
    async ({
      cartId,
      shippingMethodId,
      shippingMethodName,
      email,
      billingAddress,
      shippingAddress,
    }: FirstCheckoutStepParams) => {
      const properties = {
        ...requestSource,
        cart_id: cartId,
        step: 1,
        step_name: CheckoutSteps.STEP_1,
        shipping_method: shippingMethodName,
        shipping_method_id: shippingMethodId,
      };

      await sendSegmentTrackEvent(
        SEGMENT_EVENTS.checkoutStepCompleted,
        properties
      );

      const getAllGACookies = (): GA_COOKIES => {
        // Regex to match _ga_ cookies with their Measurement ID suffixes
        const gaCookieRegex = /^_ga_([A-Z0-9]+)$/i;

        return document.cookie
          .split(";")
          .reduce<GA_COOKIES>((ga_cookies, cookieString) => {
            const [cookieKey, cookieValue] = cookieString.trim().split("=");

            const isGACookie = cookieKey.match(gaCookieRegex);

            if (isGACookie && cookieValue) {
              const parameterKey = `cookie_google_ga_${isGACookie[1]}`;
              ga_cookies[parameterKey] = cookieValue;
            }

            return ga_cookies;
          }, {});
      };

      const data: GeneralObject = {
        email,
        cookie_google_ga: Cookies.get("_ga"),
        ...getAllGACookies(),
        cookie_facebook_fbp: Cookies.get("_fbp"),
        first_name: billingAddress.firstName,
        last_name: billingAddress.lastName,
        address: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          postal_code: billingAddress.postalCode,
          country: billingAddress.country,
          city: billingAddress.city,
          street:
            billingAddress.country === "NL"
              ? billingAddress.streetNumber
              : `${billingAddress.streetNumber || ""} ${
                  billingAddress.streetName || ""
                } ${billingAddress.additionalStreetInfo || ""}`,
          phone_number: billingAddress.phone,
        },
      };

      if (shippingAddress) {
        data.shipping_address = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          postal_code: shippingAddress.postalCode,
          country: shippingAddress.country,
          city: shippingAddress.city,
          street:
            shippingAddress.country === "NL"
              ? shippingAddress.streetNumber
              : `${shippingAddress.streetNumber || ""} ${
                  shippingAddress.streetName || ""
                } ${shippingAddress.additionalStreetInfo || ""}`,
          phone_number: shippingAddress.phone,
        };
      }

      if (isNativeApp) {
        return segmentNativeAppHandler({
          eventParams: data,
          eventType: "identify",
        });
      } else {
        const args = session?.user?.member_id
          ? { member_id: session.user.member_id, ...data }
          : { ...data };

        await segmentIdentify(args);
      }
    }
  );

  const checkoutSecondStepStarted = isClientWrapper((cart: Cart) => {
    const discount = getLineItemsDiscountPrice(
      cart?.discountCodes,
      cart.lineItems
    );

    const hashedEmail = session?.user?.email
      ? sha256Email(session?.user?.email)
      : undefined;

    pushToDataLayer({
      event: "checkout",
      checkout_step: 2,
      checkout_step_name: CheckoutSteps.STEP_2,
      cart_id: cart?.id,
      cart_value: formatStandardPrice(cart?.totalPrice),
      shipping_method: cart?.shippingInfo?.shippingMethod?.name,
      shipping_id: cart?.shippingInfo?.shippingMethod?.id,
      shipping_costs: cart?.shippingInfo
        ? getShippingCost(cart?.shippingInfo)
        : undefined,
      coupon_event_type: hasDiscountCode(cart) ? "added" : "not entered",
      coupon_code: getDiscountCode(cart),
      coupon_amount: discount
        ? formatStandardPrice(discount)
        : getTalonOneDiscountAmount(cart),
      items: getTrackingCartItems(cart?.lineItems, storeConfig),
      eid_sha256: hashedEmail,
    });
  });

  const segmentSecondCheckoutStepCompleted = isClientWrapper(
    (cartId: string, paymentMethod?: string) => {
      const properties = {
        ...requestSource,
        cart_id: cartId,
        step: 2,
        step_name: CheckoutSteps.STEP_2,
        payment_method: paymentMethod,
        // payment_bank: null,
      };

      return sendSegmentTrackEvent(
        SEGMENT_EVENTS.checkoutStepCompleted,
        properties
      );
    }
  );

  const segmentOrderCompleted = isClientWrapper(
    (data: { trackingData: CheckoutTrackingData; isOldCustomer: boolean }) => {
      const { trackingData, isOldCustomer } = data;

      const properties = {
        ...requestSource,
        cart_id: trackingData.cartId,
        currency: trackingData.currencyCode,
        shipping: formatStandardPrice(trackingData.shippingInfo?.price),
        shipping_tax_rate: trackingData.shippingInfo?.taxRate,
        tax:
          trackingData.taxedPrice?.totalTax !== undefined &&
          trackingData.taxedPrice?.totalTax !== null
            ? formatStandardPrice(trackingData.taxedPrice.totalTax)
            : null,
        totalPrice: formatStandardPrice(trackingData.taxedPrice?.totalGross),
        products: trackingData.trackingProducts,
        customer_type: isOldCustomer ? "old customer" : "new customer",
        discounts: trackingData.discountCodes,
        coupons: toTrackingCoupons(trackingData.couponCodes),
        email: trackingData.hashedEmail,
      };

      pushToDataLayer({
        event: "payment_complete",
        items: trackingData.trackingItems,
        cart_id: trackingData.cartId,
        coupon_code: trackingData.discountCode,
        coupon_amount: trackingData.couponAmount,
        shipping_method: trackingData.shippingInfo?.shippingMethodName,
        shipping_id: trackingData.shippingInfo?.shippingMethodId,
        shipping_costs: trackingData.shippingInfo?.shippingCost,
        shipping_tax_rate: trackingData.shippingInfo?.taxRate,
        payment_id: trackingData.paymentId,
        shop_currency: trackingData.currencyCode,
        order_total: formatStandardPrice(trackingData.taxedPrice?.totalGross),
        order_tax:
          trackingData.taxedPrice?.totalTax !== undefined &&
          trackingData.taxedPrice?.totalTax !== null
            ? formatStandardPrice(trackingData.taxedPrice.totalTax)
            : null,
        order_payment_type: trackingData.paymentMethod,
        discounts: trackingData.discountCodes,
        customer_type: isOldCustomer ? "old customer" : "new customer",
        eid_sha256: trackingData.hashedEmail,
      });

      return sendSegmentTrackEvent(SEGMENT_EVENTS.orderCompleted, properties);
    }
  );

  const segmentCouponApplied = isClientWrapper(
    ({
      cartTotalProducts,
      cartValue,
      couponCode,
      couponType,
      couponDiscountAbsolute,
      currency,
    }: SegmentCouponTracking & { couponDiscountAbsolute: number }) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.couponApplied, {
        ...requestSource,
        coupon_code: couponCode?.toUpperCase(),
        coupon_type: couponType,
        coupon_discount_absolute: couponDiscountAbsolute,
        currency,
        cart_value: cartValue,
        cart_total_products: cartTotalProducts,
      })
  );

  const segmentCouponDenied = isClientWrapper(
    ({
      couponCode,
      couponType,
      currency,
      cartValue,
      cartTotalProducts,
      couponDenied,
    }: SegmentCouponTracking & { couponDenied: string }) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.couponDenied, {
        ...requestSource,
        coupon_code: couponCode?.toUpperCase(),
        coupon_type: couponType,
        currency,
        coupon_message: couponDenied,
        cart_value: cartValue,
        cart_total_products: cartTotalProducts,
      })
  );

  const segmentCouponEntered = isClientWrapper(
    ({
      couponCode,
      couponType,
      currency,
      cartValue,
      cartTotalProducts,
    }: SegmentCouponTracking) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.couponEntered, {
        ...requestSource,
        coupon_code: couponCode?.toUpperCase(),
        coupon_type: couponType,
        currency,
        cart_value: cartValue,
        cart_total_products: cartTotalProducts,
      })
  );

  const segmentCouponRemoved = isClientWrapper(
    ({
      couponCode,
      couponType,
      currency,
      cartValue,
      cartTotalProducts,
    }: SegmentCouponTracking) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.couponRemoved, {
        ...requestSource,
        coupon_code: couponCode?.toUpperCase(),
        coupon_type: couponType,
        currency,
        cart_value: cartValue,
        cart_total_products: cartTotalProducts,
      })
  );

  const segmentFormError = isClientWrapper(
    (formField: string, formErrorMessage: string, formName?: string) => {
      const eventName = SEGMENT_EVENTS.formError;

      const params = {
        form_name: formName || getFormName(formField),
        form_field: formField,
        form_error_message: formErrorMessage,
        form_error_type: formErrorMessage,
      };

      if (isNativeApp) {
        return segmentNativeAppHandler({
          eventName,
          eventParams: params,
          eventType: "track",
        });
      } else {
        return sendSegmentTrackEvent(eventName, {
          ...requestSource,
          ...params,
        });
      }
    }
  );

  const segmentFormTracking = isClientWrapper(
    (formField: string, formName?: string) => {
      const eventName = SEGMENT_EVENTS.formTracking;

      const params = {
        form_name: formName || getFormName(formField),
        form_field: formField,
      };

      if (isNativeApp) {
        return segmentNativeAppHandler({
          eventName,
          eventParams: params,
          eventType: "track",
        });
      } else {
        return sendSegmentTrackEvent(eventName, {
          ...requestSource,
          ...params,
        });
      }
    }
  );

  const segmentOptIn = isClientWrapper(
    async (
      optIn: boolean,
      email: string | null | undefined,
      origin?: string
    ) => {
      const traits = {
        email: email ?? undefined,
        optIn,
      };
      if (isNativeApp) {
        await segmentNativeAppHandler({
          eventParams: traits,
          eventType: "identify",
        });
      } else {
        await segmentIdentify(traits);
      }

      if (optIn && email) {
        const hashedEmail = sha256Email(email);
        pushToDataLayer({
          event: "newsletter_subscribe",
          optin: optIn,
          eid_sha256: hashedEmail,
        });
      }

      const track = await sendSegmentTrackEvent(SEGMENT_EVENTS.optIn, {
        ...requestSource,
        optIn,
        email,
        metadata: {
          origin,
        },
      });

      return track;
    }
  );

  const segmentPageViewed = isClientWrapper(
    async ({
      pathName,
      locale,
      shopType,
      cartData,
      productAvailable,
      gtmProperties,
    }: PageViewedProps) => {
      const properties = {
        locale,
        shop_type: shopType,
        cart_total_products: cartData?.product_count,
        cart_value: cartData?.cart_total,
        ...(typeof productAvailable !== "undefined" && {
          product_page_message: productAvailable
            ? "product available"
            : "product no longer available",
        }),
        ...requestSource,
      };

      if (isNativeApp) {
        // NOTE: Temporary fix until the page title can be mapped to the URL
        const screenName =
          pathName.length > 100 ? pathName.slice(0, 100) : pathName;
        return await segmentNativeAppHandler({
          eventName: screenName,
          eventParams: properties,
          eventType: "screen",
        });
      } else {
        if (
          typeof gtmProperties?.page_location === "string" &&
          !gtmProperties.page_location.includes("signin")
        ) {
          pushToDataLayer({
            event: "page",
            ...gtmProperties,
            environment: process.env.RELEASE_STAGE,
          });
        }

        return await window.htevents?.page(pathName, properties, {
          context: { locale: localeAdapter(locale) },
        });
      }
    }
  );

  const segmentPasswordUpdated = isClientWrapper(() => {
    const params = requestSource;

    if (isNativeApp) {
      return segmentNativeAppHandler({
        eventName: SEGMENT_EVENTS.passwordUpdated,
        eventParams: params,
        eventType: "track",
      });
    } else {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.passwordUpdated, params);
    }
  });

  const segmentProductAddedOrRemoved = isClientWrapper(
    (
      lineItem: LineItem,
      type: "productAdded" | "productRemoved",
      countryCode: Iso2LetterLocale | null,
      quantity?: number,
      cartData?: {
        product_count?: number;
        cart_total?: number;
        totalPrice?: number;
        price?: number;
        discountPrice?: number | null;
      },
      lineItems?: LineItem[]
    ) => {
      const attributesRaw = lineItem.variant?.attributesRaw;

      const selectedSize = getCTAttribute("size_value", attributesRaw);

      const getRegularPrice = (prices: ProductPrice[] | undefined | null) => {
        const localPrice = getLineItemPrice(prices, countryCode);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const regularPriceInCents = localPrice?.custom?.customFieldsRaw?.find(
          (field) => field.name === "list_price"
        )?.value.centAmount as number;

        return regularPriceInCents / 100;
      };

      pushToDataLayer({
        event: type === "productRemoved" ? "remove_from_cart" : "add_to_cart",
        item_id: getCTAttribute("woo_product_id", attributesRaw),
        item_sku: lineItem?.variant?.sku,
        item_name: getCTAttribute("name", attributesRaw),
        item_variant_sku: lineItem?.variant?.sku,
        item_variant_name: selectedSize,
        brand_id: getCTAttribute("woo_brand_id", attributesRaw),
        brand_name: getCTAttribute("brand_name", attributesRaw),
        quantity: (quantity || lineItem.quantity) as number,
        price: lineItem.price.value.centAmount / 100,
        retail_price: cartData?.totalPrice,
        discount_value: cartData?.discountPrice,
        discount_type: "absolute",
        item_custom_labels: itemLabels(
          getCTAttribute("labels", attributesRaw) as unknown as Labels
        ),
        email: session?.user?.email,
      });

      return sendSegmentTrackEvent(SEGMENT_EVENTS[type], {
        ...requestSource,
        brand: getCTAttribute("brand_name", attributesRaw),
        brand_id: getCTAttribute("woo_brand_id", attributesRaw),
        // cart_total_products: null,
        // cart_total_value: null,
        category: [],
        category_id: [],
        currency: lineItem.price.value.currencyCode as string,
        // free_shipping_value: null,
        image_url: getCTAttribute("main_image", attributesRaw),
        labels: getCTAttribute("labels", attributesRaw),
        name: getCTAttribute("name", attributesRaw),
        price: lineItem.price.value.centAmount / 100,
        // product_discount: null,
        product_id: getCTAttribute("woo_product_id", attributesRaw),
        // product_kids_gender: null,
        // product_shop_type: null,
        product_size: selectedSize,
        quantity: (quantity || lineItem.quantity) as number,
        regular_price: getRegularPrice(lineItem?.variant?.prices),
        sku: lineItem?.variant?.sku,
        url: `product/${lineItem.productSlug as string}`,
        variant: selectedSize,
        cart_total_products: cartData?.product_count,
        cart_value: cartData?.cart_total,
        ...(SEGMENT_EVENTS[type] === SEGMENT_EVENTS.productAdded && {
          prod_sequence: getCartItemsCount(lineItems),
        }),
        email: session?.user?.email,
      });
    }
  );

  const segmentProductListFilterOpened = isClientWrapper((filterName: string) =>
    sendSegmentTrackEvent(SEGMENT_EVENTS.productListFilterOpened, {
      filter_type_open: getGenericFilterName(filterName),
    })
  );

  const segmentProductOutOfStock = isClientWrapper(
    (
      product: SingleProduct | SingleCartProductVariation,
      size?: string | null
    ) => {
      const selectedSize = size || SEGMENT_DATA_UNAVAILABLE;

      const {
        image,
        price_float,
        product_name,
        product_slug,
        quantity_in_cart,
        regular_price_float,
      } = product as SingleCartProductVariation;

      const { images, name, price, regular_price, slug } =
        product as SingleProduct;

      const imageURL = image || images?.[0] || SEGMENT_DATA_UNAVAILABLE;
      const productName = product_name || name || SEGMENT_DATA_UNAVAILABLE;
      const productSlug = product_slug || slug || SEGMENT_DATA_UNAVAILABLE;
      const productPrice = price_float || price || SEGMENT_DATA_UNAVAILABLE;
      const regularPrice =
        regular_price_float || regular_price || SEGMENT_DATA_UNAVAILABLE;

      return sendSegmentTrackEvent(SEGMENT_EVENTS.productOutOfStock, {
        ...requestSource,
        brand: product.brand.name,
        brand_id: product.brand.id,
        category: product?.translated_category_names || [],
        category_id: product?.translated_category_ids || [],
        currency: product.currency.code,
        image_url: imageURL,
        labels: (product?.labels || []).map((label) => label?.value),
        name: productName,
        price: productPrice,
        product_discount: product.product_discount,
        product_id: product.id,
        product_kids_gender: product.product_shop_type?.includes("kids")
          ? product.product_kids_gender
          : undefined,
        product_shop_type: product.product_shop_type,
        product_size: selectedSize,
        quantity: quantity_in_cart,
        regular_price: regularPrice,
        sku: product.sku,
        url: `product/${productSlug}`,
        variant: selectedSize,
        gender: product.gender,
      });
    }
  );

  const segmentProductSizeSelected = isClientWrapper(
    (product: SingleProduct, size?: string | null, resultId?: string) => {
      const selectedSize = size || SEGMENT_DATA_UNAVAILABLE;

      const sizesAvailable = (product.variations || [])
        .filter((variation) => variation.stock_quantity > 0)
        .map((val) => val.size);

      return sendSegmentTrackEvent(SEGMENT_EVENTS.productSizeSelected, {
        ...requestSource,
        brand: product.brand.name,
        brand_id: product.brand.id,
        category: product?.translated_category_names || [],
        category_id: product?.translated_category_ids || [],
        currency: product.currency.code,
        image_url: product.images?.[0],
        labels: (product.labels || []).map(({ value }) => value),
        name: product.name,
        price: product.price,
        product_discount: product.product_discount,
        product_id: product.id,
        product_kids_gender: product.product_shop_type?.includes("kids")
          ? product.product_kids_gender
          : undefined,
        product_shop_type: product.product_shop_type,
        product_size: selectedSize,
        regular_price: product.regular_price,
        sizes_available: sizesAvailable,
        sku: product.sku,
        url: `product/${product.slug}`,
        variant: selectedSize,
        gender: product.gender,
        result_id: resultId,
      });
    }
  );

  const segmentProductSizeSelectorClicked = isClientWrapper(
    (product: SingleProduct, resultId?: string) => {
      const sizesAvailable = (product.variations || [])
        .filter((variation) => variation.stock_quantity > 0)
        .map((val) => val.size);

      return sendSegmentTrackEvent(SEGMENT_EVENTS.productSizeSelectorClicked, {
        ...requestSource,
        brand: product.brand.name,
        brand_id: product.brand.id,
        category: product?.translated_category_names || [],
        category_id: product?.translated_category_ids || [],
        currency: product.currency.code,
        image_url: product.images?.[0],
        labels: (product.labels || []).map(({ value }) => value),
        name: product.name,
        price: product.price,
        product_discount: product.product_discount,
        product_id: product.id,
        product_kids_gender: product.product_shop_type?.includes("kids")
          ? product.product_kids_gender
          : undefined,
        product_shop_type: product.product_shop_type,
        regular_price: product.regular_price,
        sizes_available: sizesAvailable,
        url: `product/${product.slug}`,
        gender: product.gender,
        result_id: resultId,
      });
    }
  );

  const segmentProductViewed = isClientWrapper(
    (
      product: SingleProduct,
      isLoggedIn?: boolean,
      gatedActionType?: string,
      redirectUrl?: string,
      actionSource?: string
    ) => {
      pushToDataLayer({
        event: "view_item",
        items: [
          {
            item_id: product.id,
            item_sku: product.sku,
            item_name: product.name,
            item_variant_sku: product.variations?.[0]?.id,
            item_variant_name: product.variations?.[0]?.size,
            brand_id: product.brand.id,
            brand_name: product.brand.name,
            item_quantity: 1,
            item_category: product?.translated_category_ids?.length
              ? product.translated_category_ids.map((id, index) => ({
                  level: (index + 1).toString(),
                  name: product?.translated_category_names?.length
                    ? product?.translated_category_names[index]
                    : "",
                  id,
                }))
              : [],
            item_availability: product.variations?.some(
              (item) => item.stock_quantity > 1
            ),
            price: product.price,
            retail_price: product.regular_price,
            discount_value: product.product_discount,
            discount_type: "absolute",
            item_custom_labels: (product.labels || []).map(
              ({ type, value }) => {
                return { label_id: type, label_name: value };
              }
            ),
          },
        ],
      });

      return sendSegmentTrackEvent(
        gatedActionType
          ? SEGMENT_EVENTS.gatedPopupRedirected
          : SEGMENT_EVENTS.productViewed,
        {
          ...requestSource,
          brand: product.brand.name,
          brand_id: product.brand.id,
          category: product?.translated_category_names || [],
          category_id: product?.translated_category_ids || [],
          currency: product?.currency?.code,
          image_url: product.images?.[0],
          labels: (product.labels || []).map(({ value }) => value),
          name: product.name,
          price: product.price,
          product_discount: product.product_discount,
          product_id: product.id,
          product_kids_gender: product.product_shop_type?.includes("kids")
            ? product.product_kids_gender
            : undefined,
          product_shop_type: product.product_shop_type,
          regular_price: product.regular_price,
          sizes_available: product.variations
            ? getAvailableSizes(product.variations)
            : undefined,
          url: `product/${product.slug}`,
          gender: product.gender,
          product_variation: product.variations?.[0]?.id,
          ...(gatedActionType && { gated_action_type: gatedActionType }),
          gate_level: product.gate?.level || null,
          ...(redirectUrl && { redirect_to: redirectUrl }),
          action_source: actionSource,
          gated: !isLoggedIn,
          email: session?.user?.email,
        }
      );
    }
  );

  const segmentPromotionClickedOrViewed = isClientWrapper(
    ({
      id,
      name,
      creative,
      position,
      eventType,
      tab,
      promotionType,
      gatedActionType,
      shopType,
      redirectTo,
      path,
      gated,
    }: Promotion & {
      tab?: string;
      gatedActionType?: string;
      shopType?: string;
      redirectTo?: string;
    }) => {
      return sendSegmentTrackEvent(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        gatedActionType
          ? SEGMENT_EVENTS.gatedPopupRedirected
          : eventType === PromotionEventType.click
          ? SEGMENT_EVENTS.clickedPromotion
          : SEGMENT_EVENTS.viewedPromotion,
        {
          ...requestSource,
          promo_id: id,
          promo_name: name,
          promo_creative: creative,
          promo_position: position,
          promo_type: promotionType,
          promo_path: path,
          shop_type: shopType,
          ...(redirectTo && { redirect_to: redirectTo }),
          gated_action_type: gatedActionType,
          tab,
          gated,
        }
      );
    }
  );

  const segmentUserEmailUpdated = isClientWrapper(() => {
    if (isNativeApp) {
      return segmentNativeAppHandler({
        eventName: SEGMENT_EVENTS.userEmailUpdated,
        eventParams: requestSource,
        eventType: "track",
      });
    } else {
      return sendSegmentTrackEvent(
        SEGMENT_EVENTS.userEmailUpdated,
        requestSource
      );
    }
  });

  const segmentProductOutOfStockCT = isClientWrapper(
    (
      lineItem: LineItem,
      warning: string,
      countryCode: Iso2LetterLocale | null
    ) => {
      const attributesRaw = lineItem.variant?.attributesRaw;

      const selectedSize = getCTAttribute("size_value", attributesRaw);

      const getRegularPrice = (prices: ProductPrice[] | undefined | null) => {
        const localPrice = getLineItemPrice(prices, countryCode);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const regularPriceInCents = localPrice?.custom?.customFieldsRaw?.find(
          (field) => field.name === "list_price"
        )?.value.centAmount as number;

        return regularPriceInCents / 100;
      };

      return sendSegmentTrackEvent(SEGMENT_EVENTS.productOutOfStockCT, {
        ...requestSource,
        brand: getCTAttribute("brand_name", attributesRaw),
        brand_id: getCTAttribute("woo_brand_id", attributesRaw),
        category: [],
        category_id: [],
        image_url: getCTAttribute("main_image", attributesRaw),
        labels: getCTAttribute("labels", attributesRaw),
        name: getCTAttribute("name", attributesRaw),
        price: lineItem.price.value.centAmount / 100,
        // product_discount: null,
        product_id: getCTAttribute("woo_product_id", attributesRaw),
        // product_kids_gender: null,
        // product_shop_type: null,
        product_size: selectedSize,
        quantity: lineItem.quantity as number,
        regular_price: getRegularPrice(lineItem?.variant?.prices),
        sku: lineItem?.variant?.sku,
        url: `product/${lineItem.productSlug as string}`,
        variant: selectedSize,
        warning,
      });
    }
  );

  const segmentDomainChanged = isClientWrapper(
    (domainName: string, callback: () => void = () => {}) =>
      sendSegmentTrackEvent(
        SEGMENT_EVENTS.domainChanged,
        {
          domain_selected: domainName,
        },
        callback
      )
  );

  const segmentToasterNotificationDisplayed = isClientWrapper(
    (promo_name: string | undefined, promo_path?: string) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.viewedPromotion, {
        promo_name,
        promo_position: "toaster-notification-1",
        promotion_type: SEGMENT_PROMOTION_TYPE.toasterNotification,
        promo_path,
      })
  );

  const segmentToasterNotificationDismissed = isClientWrapper(
    (promo_name: string | undefined, promo_path?: string) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.notificationDismissed, {
        promo_name,
        promo_position: "toaster-notification-1",
        promo_path,
      })
  );

  const segmentToasterNotificationClicked = isClientWrapper(
    (promo_name: string | undefined, promo_path?: string) =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.clickedPromotion, {
        promo_name,
        promo_position: "toaster-notification-1",
        promotion_type: SEGMENT_PROMOTION_TYPE.toasterNotification,
        promo_path,
      })
  );

  const segmentSearchOverlayOpened = isClientWrapper(() =>
    sendSegmentTrackEvent(SEGMENT_EVENTS.searchOverlayOpen, {})
  );

  const segmentSearchOverlayClosed = isClientWrapper(
    (closedVia: "click x" | "click outside") =>
      sendSegmentTrackEvent(SEGMENT_EVENTS.searchOverlayClose, {
        search_close: closedVia,
      })
  );

  const segmentSearchRecommendationClicked = useCallback(
    (
      ...args: Parameters<
        UseSegmentReturnType["segmentSearchRecommendationClicked"]
      >
    ) =>
      isClientWrapper<
        UseSegmentReturnType["segmentSearchRecommendationClicked"]
      >((search_rec_type, search_rec_name, search_rec_position) =>
        sendSegmentTrackEvent(SEGMENT_EVENTS.searchRecommendationClicked, {
          search_rec_type,
          search_rec_name,
          search_rec_position,
        })
      )(...args),
    [sendSegmentTrackEvent]
  );

  const segmentViewSearchAllBrands = useCallback(
    (...args: Parameters<UseSegmentReturnType["segmentViewSearchAllBrands"]>) =>
      isClientWrapper(() =>
        sendSegmentTrackEvent(SEGMENT_EVENTS.viewSearchAllBrands, {})
      ),
    [sendSegmentTrackEvent]
  );

  const segmentViewSearchResultPage = isClientWrapper(() =>
    sendSegmentTrackEvent(SEGMENT_EVENTS.viewSearchResults, {})
  );

  const trackForgotPassword = isClientWrapper(() =>
    sendSegmentTrackEvent(SEGMENT_EVENTS.forgotPasswordFlowStarted, {})
  );

  const segmentPdpInfoBoxOpened = isClientWrapper((boxName: string) =>
    sendSegmentTrackEvent(SEGMENT_EVENTS.pdpInfoBoxOpened, {
      info_box_name: boxName,
    })
  );

  const segmentUserProfileUpdated = isClientWrapper(
    async (userData: UserUpdateData): Promise<void> => {
      if (!session?.user?.member_id) return;

      await Promise.all([
        sendSegmentTrackEvent(SEGMENT_EVENTS.userProfileUpdated, userData),
        segmentIdentify({
          first_name: userData.firstName,
          last_name: userData.lastName,
          birthday: userData.birthday,
          gender: userData.gender,
          phone_number: userData.phoneNumber,
          address: {
            postalCode: userData.address?.postalCode,
            country: userData.address?.country,
            city: userData.address?.city,
            street: userData.address?.street,
          },
        }),
      ]);
    }
  );

  const segmentViewedAuthBanner = isClientWrapper((page_name?: string) => {
    return sendSegmentTrackEvent(SEGMENT_EVENTS.authBannerViewed, {
      page_information_name: page_name,
    });
  });

  const segmentAuthBannerSelect = isClientWrapper(
    (data: AuthBannerData & { type: "sign_up" | "sign_in" }) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.authBannerSelect, {
        page_type:
          typeof data.page_type === "string" &&
          isProductListType(data.page_type)
            ? PRODUCT_LISTING_TYPE[data.page_type]
            : data.page_type,
        brand: data.brand,
        category: data.category,
        category_slug: data.category_slug,
        type: data.type,
        shop_type: data.shop_type,
      });
    }
  );

  const segmentAuthBannerClosed = isClientWrapper(
    (data: AuthBannerData & { popup_type: "sign_up" | "sign_in" }) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.authPopupClosed, {
        page_type:
          typeof data.page_type === "string" &&
          isProductListType(data.page_type)
            ? PRODUCT_LISTING_TYPE[data.page_type]
            : data.page_type,
        brand: data.brand,
        category: data.category,
        category_slug: data.category_slug,
        popup_type: data.popup_type,
        shop_type: data.shop_type,
      });
    }
  );

  const segmentGuestOptInBannerDismissed = isClientWrapper(
    (page_name?: string) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.guestOptInBannerDismissed, {
        page_information_name: page_name,
      });
    }
  );

  const segmentGuestOptInBannerViewed = isClientWrapper(
    (page_name?: string) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.guestOptInBannerViewed, {
        page_information_name: page_name,
      });
    }
  );

  const segmentGuestOptInBannerCtaClick = isClientWrapper(
    (page_name?: string) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.guestOptInBannerCtaClick, {
        page_information_name: page_name,
      });
    }
  );

  const segmentGuestOptInPopupDismissed = isClientWrapper(
    (page_name?: string) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.guestOptInPopupDismissed, {
        page_information_name: page_name,
      });
    }
  );

  const trackingGooglePayButtonClicked = isClientWrapper((cart_id: string) => {
    return sendSegmentTrackEvent(SEGMENT_EVENTS.googlePayButtonClicked, {
      ...requestSource,
      cart_id,
      email: session?.user?.email,
    });
  });

  const trackingCategoryItemClicked = isClientWrapper(
    (category_slug: string, category_name: string) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.categoryItemClicked, {
        ...requestSource,
        category_slug,
        category_name,
      });
    }
  );

  const eppoRandomizedAssigment = isClientWrapper(
    (assignment: IAssignmentEvent) => {
      const { featureFlag, variation, ...rest } = assignment;
      const properties =
        featureFlag === "alert-banner-shopping-cart-web"
          ? {
              ...rest,
              featureFlag,
              variation: variation ? JSON.parse(variation) : null,
            }
          : assignment;

      return sendSegmentTrackEvent(SEGMENT_EVENTS.eppoRandomizedAssignment, {
        ...requestSource,
        userId: assignment.subject,
        event: "Eppo Randomized Assignment",
        type: "track",
        ...properties,
      });
    }
  );

  const segmentTrackingError = isClientWrapper(
    ({
      error_type,
      error_message,
    }: {
      error_type: string;
      error_message: string;
    }) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.error, {
        ...requestSource,
        error_type,
        error_message,
      });
    }
  );

  const segmentMenuNavClicked = isClientWrapper(
    (
      menuType: string,
      menuClickType: string,
      menuClickName: string,
      menuSectionName?: string
    ) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.menuNavClicked, {
        ...requestSource,
        menu_type: menuType,
        menu_click_type: menuClickType,
        menu_section_name: menuSectionName,
        menu_click_name: menuClickName,
      });
    }
  );

  const segmentOptInDeeplinkViewed = isClientWrapper(() => {
    return sendSegmentTrackEvent(SEGMENT_EVENTS.optInDeeplinkViewed, {});
  });

  const segmentGatedPopupOpened = isClientWrapper(
    ({
      pageName,
      shopType,
      actionSource,
      carouselType,
      productName,
      productId,
      redirectTo,
      productImageUrl,
      brandName,
      brandId,
      brandImageUrl,
      level,
      tab,
    }: GatedPopupData) => {
      if (brandId) {
        return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupOpened, {
          ...requestSource,
          page_information_name: pageName,
          shop_type: shopType,
          action_source: actionSource,
          carousel_type: carouselType,
          brand_name: brandName,
          brand_id: brandId,
          brand_image_url: brandImageUrl,
          ...(redirectTo && { redirect_to: redirectTo }),
          gate_level: level,
          tab,
        });
      }
      return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupOpened, {
        ...requestSource,
        page_information_name: pageName,
        shop_type: shopType,
        action_source: actionSource,
        carousel_type: carouselType,
        product_name: productName,
        product_id: productId,
        product_image_url: productImageUrl,
        ...(redirectTo && { redirect_to: redirectTo }),
        gate_level: level,
        tab,
      });
    }
  );

  const segmentGatedPopupLoginClicked = isClientWrapper(
    ({
      pageName,
      shopType,
      actionSource,
      carouselType,
      redirectTo,
      productName,
      productId,
      productImageUrl,
      brandName,
      brandId,
      brandImageUrl,
      level,
      tab,
    }: GatedPopupData) => {
      if (brandId) {
        return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupLoginClicked, {
          ...requestSource,
          page_information_name: pageName,
          shop_type: shopType,
          action_source: actionSource,
          carousel_type: carouselType,
          ...(redirectTo && { redirect_to: redirectTo }),
          brand_name: brandName,
          brand_id: brandId,
          brand_image_url: brandImageUrl,
          gate_level: level,
          tab,
        });
      }
      return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupLoginClicked, {
        ...requestSource,
        page_information_name: pageName,
        shop_type: shopType,
        action_source: actionSource,
        carousel_type: carouselType,
        ...(redirectTo && { redirect_to: redirectTo }),
        product_name: productName,
        product_id: productId,
        product_image_url: productImageUrl,
        gate_level: level,
        tab,
      });
    }
  );

  const segmentGatedPopupRegisterClicked = isClientWrapper(
    ({
      pageName,
      shopType,
      actionSource,
      carouselType,
      redirectTo,
      productName,
      productId,
      productImageUrl,
      brandName,
      brandId,
      brandImageUrl,
      level,
      tab,
    }: GatedPopupData) => {
      if (brandId) {
        return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupRegisterClicked, {
          ...requestSource,
          page_information_name: pageName,
          shop_type: shopType,
          action_source: actionSource,
          carousel_type: carouselType,
          ...(redirectTo && { redirect_to: redirectTo }),
          brand_name: brandName,
          brand_id: brandId,
          brand_image_url: brandImageUrl,
          gate_level: level,
          tab,
        });
      }
      return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupRegisterClicked, {
        ...requestSource,
        page_information_name: pageName,
        shop_type: shopType,
        action_source: actionSource,
        carousel_type: carouselType,
        ...(redirectTo && { redirect_to: redirectTo }),
        product_name: productName,
        product_id: productId,
        product_image_url: productImageUrl,
        gate_level: level,
        tab,
      });
    }
  );

  const segmentGatedPopupClosed = isClientWrapper(
    ({
      pageName,
      shopType,
      actionSource,
      carouselType,
      productName,
      productId,
      productImageUrl,
      redirectTo,
      brandName,
      brandId,
      brandImageUrl,
      level,
      tab,
    }: GatedPopupData) => {
      if (brandId) {
        return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupClosed, {
          ...requestSource,
          page_information_name: pageName,
          shop_type: shopType,
          action_source: actionSource,
          carousel_type: carouselType,
          brand_name: brandName,
          brand_id: brandId,
          brand_image_url: brandImageUrl,
          gate_level: level,
          ...(redirectTo && { redirect_to: redirectTo }),
          tab,
        });
      }
      return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupClosed, {
        ...requestSource,
        page_information_name: pageName,
        shop_type: shopType,
        action_source: actionSource,
        carousel_type: carouselType,
        product_name: productName,
        product_id: productId,
        product_image_url: productImageUrl,
        gate_level: level,
        ...(redirectTo && { redirect_to: redirectTo }),
        tab,
      });
    }
  );

  const listViewAllSelected = isClientWrapper<
    UseSegmentReturnType["listViewAllSelected"]
  >((itemValues, button_position) =>
    sendSegmentTrackEvent(SEGMENT_EVENTS.listViewAllSelected, {
      ...requestSource,
      item: {
        ...itemValues,
        list_content_type: "product",
        list_id: "search_overlay_product_results",
      },
      button_position,
    })
  );

  const searchResultDisplayed = useCallback(
    (...args: Parameters<UseSegmentReturnType["searchResultDisplayed"]>) =>
      isClientWrapper<UseSegmentReturnType["searchResultDisplayed"]>((props) =>
        sendSegmentTrackEvent(SEGMENT_EVENTS.searchResultDisplayed, {
          ...requestSource,
          ...props,
          traits: { email: session?.user?.email },
        })
      )(...args),
    [sendSegmentTrackEvent, session?.user?.email]
  );

  const searchResultClicked = isClientWrapper<
    UseSegmentReturnType["searchResultClicked"]
  >((props) =>
    sendSegmentTrackEvent(SEGMENT_EVENTS.searchResultClicked, {
      ...requestSource,
      ...props,
      traits: { email: session?.user?.email },
    })
  );

  const searchAbandoned = isClientWrapper<
    UseSegmentReturnType["searchAbandoned"]
  >((props) => {
    return sendSegmentTrackEvent(SEGMENT_EVENTS.searchAbandoned, {
      ...requestSource,
      ...props,
      traits: { email: session?.user?.email },
    });
  });

  const productsSearched = isClientWrapper<
    UseSegmentReturnType["productsSearched"]
  >((query: string) =>
    sendSegmentTrackEvent(SEGMENT_EVENTS.productsSearched, {
      ...requestSource,
      query,
      traits: { email: session?.user?.email },
    })
  );

  const segmentUserDeleted = isClientWrapper(() => {
    if (isNativeApp) {
      return segmentNativeAppHandler({
        eventName: SEGMENT_EVENTS.userDeleted,
        eventParams: requestSource,
        eventType: "track",
      });
    }

    return sendSegmentTrackEvent(SEGMENT_EVENTS.userDeleted, requestSource);
  });

  const gatedSearchResultRedirected = isClientWrapper(
    ({
      gatedActionType,
      actionSource,
      redirectTo,
      brandName,
      gateLevel,
      shopType,
    }: {
      gatedActionType?: string;
      actionSource: string;
      redirectTo?: string;
      brandName?: string;
      gateLevel?: number;
      shopType?: string;
    }) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedSearchResultRedirected, {
        ...requestSource,
        action_source: actionSource,
        gated_action_type: gatedActionType,
        brand_name: brandName,
        shop_type: shopType,
        gate_level: gateLevel,
        redirect_to: redirectTo,
      });
    }
  );

  const gatedPopupRedirected = isClientWrapper(
    ({
      promo_type,
      shop_type,
      gated_action_type,
      redirect_to,
    }: GatedPopupRedirectData) => {
      return sendSegmentTrackEvent(SEGMENT_EVENTS.gatedPopupRedirected, {
        ...requestSource,
        promo_type,
        shop_type,
        gated_action_type,
        redirect_to,
      });
    }
  );

  const consentStatusUpdated = isClientWrapper(
    (preferences: CategoryPreferences) => {
      const transformKeys = (key: string) => {
        if (key === "marketingAndAnalytics") {
          return "analytics||marketing";
        }
        return key;
      };

      const consentStatus = preferences
        ? Object.keys(preferences)
            .filter((key) => preferences?.[key])
            .map(transformKeys)
            .join("||")
        : undefined;

      pushToDataLayer({
        event: "user_consent_update",
        consent_status: consentStatus || "functional",
      });
    }
  );

  const continueAsGuest = isClientWrapper(() => {
    return sendSegmentTrackEvent(SEGMENT_EVENTS.continueAsGuest, {});
  });

  return {
    user,
    segmentIdentify,
    segmentBrandConsciousHowWeRateClicked,
    segmentBrandInfoOpened,
    brandFavourited,
    brandUnfavourited,
    brandFavouritedWhenLoggedOut,
    segmentSignedIn,
    segmentSignedOut,
    segmentSignedUp,
    segmentCheckoutSignedUp,
    segmentCartViewed,
    checkoutStarted,
    checkoutFirstStepCompleted,
    checkoutSecondStepStarted,
    segmentSecondCheckoutStepCompleted,
    segmentOrderCompleted,
    segmentCouponApplied,
    segmentCouponDenied,
    segmentCouponEntered,
    segmentCouponRemoved,
    segmentFormError,
    segmentFormTracking,
    segmentOptIn,
    segmentPageViewed,
    segmentProductListClicked,
    segmentProductClicked,
    segmentPasswordUpdated,
    segmentProductAddedOrRemoved,
    segmentProductListFilterOpened,
    segmentProductOutOfStock,
    segmentProductSizeSelected,
    segmentProductSizeSelectorClicked,
    segmentProductViewed,
    segmentPromotionClickedOrViewed,
    segmentUserEmailUpdated,
    segmentProductOutOfStockCT,
    segmentDomainChanged,
    segmentToasterNotificationDisplayed,
    segmentToasterNotificationDismissed,
    segmentToasterNotificationClicked,
    segmentSearchOverlayOpened,
    segmentSearchOverlayClosed,
    segmentSearchRecommendationClicked,
    segmentViewSearchAllBrands,
    segmentViewSearchResultPage,
    trackForgotPassword,
    segmentPdpInfoBoxOpened,
    segmentUserProfileUpdated,
    segmentViewedAuthBanner,
    segmentAuthBannerSelect,
    segmentAuthBannerClosed,
    segmentGuestOptInBannerDismissed,
    segmentGuestOptInBannerViewed,
    segmentGuestOptInBannerCtaClick,
    segmentGuestOptInPopupDismissed,
    segmentTrackingError,
    trackingGooglePayButtonClicked,
    trackingCategoryItemClicked,
    eppoRandomizedAssigment,
    segmentMenuNavClicked,
    segmentOptInDeeplinkViewed,
    segmentGatedPopupOpened,
    segmentGatedPopupLoginClicked,
    segmentGatedPopupRegisterClicked,
    segmentGatedPopupClosed,
    segmentUserDeleted,
    listViewAllSelected,
    searchResultDisplayed,
    searchResultClicked,
    searchAbandoned,
    productsSearched,
    gatedSearchResultRedirected,
    gatedPopupRedirected,
    consentStatusUpdated,
    continueAsGuest,
  };
}
